"use client";

import { Circle } from "lucide-react";
import Link from "next/link";
import { toast } from "sonner";
import { FadeInUp } from "@/components/ui/motion";
import Image from "next/image";

const footerLinks = {
  product: [
    { label: "Features", href: "/features" },
    { label: "Integrations", href: "/integrations" },
    { label: "Security", href: "/security" },
    { label: "Templates", href: "/templates" },
    { label: "Pricing", href: "/pricing" },
  ],
  resources: [
    { label: "For Developers", href: "/developers" },
    { label: "Documentation", href: "#", comingSoon: true },
  ],
  company: [
    { label: "About", href: "/about" },
    { label: "FAQ", href: "/faq" },
    { label: "Contact", href: "/contact" },
  ],
};

export function Footer() {
  const handleComingSoon = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    toast.error("This feature is currently in development.");
  };

  return (
    <footer className="border-t border-border bg-card">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12">
          {/* Logo */}
          <FadeInUp className="col-span-2 md:col-span-1">
            <Link href="/" className="flex items-center gap-2.5 mb-4">
              {/* <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  className="w-4 h-4 text-primary-foreground"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
              </div> */}
              <Image src={'/logo/logo.png'} alt="Makeplans Logo" width={32} height={32} />
              <span className="text-lg font-semibold text-foreground">
                Makeplans
              </span>
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Workflow automation with clarity and reliability.
            </p>
          </FadeInUp>

          <FadeInUp delay={0.1}>
            <h4 className="text-sm font-semibold text-foreground mb-4">
              Product
            </h4>
            <ul className="space-y-2">
              {footerLinks.product.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </FadeInUp>

          <FadeInUp delay={0.2}>
            <h4 className="text-sm font-semibold text-foreground mb-4">
              Resources
            </h4>
            <ul className="space-y-2">
              {footerLinks.resources.map((link) => (
                <li key={link.label}>
                  {link.comingSoon ? (
                    <a
                      href="#"
                      onClick={handleComingSoon}
                      className="text-sm text-muted-foreground/60 hover:text-muted-foreground transition-colors cursor-pointer flex items-center gap-1.5"
                    >
                      {link.label}
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                        Soon
                      </span>
                    </a>
                  ) : (
                    <Link
                      href={link.href}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </FadeInUp>

          <FadeInUp delay={0.3}>
            <h4 className="text-sm font-semibold text-foreground mb-4">
              Company
            </h4>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </FadeInUp>
        </div>

        <FadeInUp delay={0.4}>
          <div className="mt-12 pt-6 border-t border-border">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                <p className="text-sm text-muted-foreground">
                  © {new Date().getFullYear()} Makeplans. All rights reserved.
                </p>
                <div className="flex items-center gap-4">
                  <Link
                    href="/privacy"
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Privacy Policy
                  </Link>
                  <Link
                    href="/terms"
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Terms of Service
                  </Link>
                </div>
              </div>
              {/* Status indicator */}
              <a
                href="#"
                className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors group"
                onClick={(e) => {
                  e.preventDefault();
                  // Could link to status page
                }}
              >
                <Circle className="w-2 h-2 fill-success text-success" />
                <span>All systems operational</span>
              </a>
            </div>
          </div>
        </FadeInUp>
      </div>
    </footer>
  );
}
